import React, { useEffect, useMemo } from 'react';
import { AlertTriangle, RefreshCw, Home, LifeBuoy } from 'lucide-react';
// Import primitives directly (not via the ./ui barrel) so this page — which is
// pulled into the marketing entry by the lazy /500 route's static reach-through
// from ErrorBoundary — does not drag Modal -> motion/react into the boot graph.
import { GlassPanel } from '../components/ui/GlassPanel';
import { LabelCaps } from '../components/ui/LabelCaps';
import { SEOHead } from '../lib/seo';
import { track, Events } from '../lib/analytics';

export interface ServerErrorProps {
  /**
   * Optional short hash identifying the underlying error (e.g. the first 8 chars
   * of a sha256 of the stack trace). Surfaced to the user so they can include
   * it when contacting support, but never exposes raw stack contents.
   */
  errorHash?: string;
  /**
   * Optional callback to reset the error state (e.g. retry the failed render).
   * When provided, "Try again" calls this instead of reloading the whole page.
   */
  onReset?: () => void;
}

/**
 * Branded 5xx / render-error page. Used directly for prod 500 routes and as the
 * default fallback inside {@link ErrorBoundary}. Tracks `error_shown` with
 * kind=500 and the incident hash so we can join PostHog product events with
 * Sentry stack traces when triaging.
 */
export const ServerError: React.FC<ServerErrorProps> = ({ errorHash, onReset }) => {
  // Stable timestamp so a re-render (e.g. style update) doesn't make the
  // displayed incident time jump on the user.
  const incidentAt = useMemo(() => new Date().toISOString(), []);

  useEffect(() => {
    try {
      track(Events.ERROR_SHOWN, {
        kind: '500',
        error_hash: errorHash ?? null,
        incident_at: incidentAt,
      });
    } catch {
      // Tracking failures must never crash the page.
    }
    // Only fire once per mount.
  }, []);

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <SEOHead
        title="Something went wrong — RealtyIQ.io"
        description="We're investigating an unexpected error. Try refreshing the page, or contact support@realtyiq.io if the issue persists."
        noIndex
      />
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-md bg-rose-50 border border-rose-200 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-rose-600" />
          </div>
          <LabelCaps className="mb-2">Error 500</LabelCaps>
          <h1 className="text-3xl text-indigo-950 mb-3" style={{ fontFamily: 'var(--font-display)' }}>
            Something went wrong on our end
          </h1>
          <p className="text-slate-600">
            We've been notified through Sentry and are looking into it. Try refreshing — most one-off glitches clear right up.
          </p>
        </div>

        <GlassPanel className="p-6">
          {(errorHash || incidentAt) && (
            <div className="mb-4 text-xs text-slate-500 border border-indigo-950/10 rounded-md p-3 bg-slate-50/50">
              <div className="font-medium text-slate-600 mb-1">Incident details (include when contacting support)</div>
              <div className="font-mono text-[11px] space-y-0.5">
                {errorHash && <div>error: {errorHash}</div>}
                <div>time: {incidentAt}</div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <a
              href="/dashboard"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-indigo-950/15 rounded-md text-slate-700 hover:bg-indigo-50/50 transition-colors text-sm font-medium"
            >
              <Home className="w-4 h-4" /> Back to dashboard
            </a>
            <a
              href="mailto:support@realtyiq.io"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-indigo-950/15 rounded-md text-slate-700 hover:bg-indigo-50/50 transition-colors text-sm font-medium"
            >
              <LifeBuoy className="w-4 h-4" /> Contact support
            </a>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
};
